import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import './ClaimStudioProfile.css';

export default function ManageStudioProfile({ studioName, user, studioProfileData, onProfileUpdated }) {
  // Form state
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Profile fields
  const [displayName, setDisplayName] = useState('');
  const [yearEstablished, setYearEstablished] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [instagram, setInstagram] = useState('');
  const [facebook, setFacebook] = useState('');
  const [tiktok, setTiktok] = useState('');
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [currentLogoURL, setCurrentLogoURL] = useState('');

  // Load current profile data
  useEffect(() => {
    if (studioProfileData) {
      setDisplayName(studioProfileData.displayName || studioName); // Default to original name if no display name set
      setYearEstablished(studioProfileData.yearEstablished || '');
      setWebsite(studioProfileData.website || '');
      setDescription(studioProfileData.description || '');
      setCurrentLogoURL(studioProfileData.logoURL || '');
      
      // Load social media data
      const socialMedia = studioProfileData.socialMedia || {};
      setInstagram(socialMedia.instagram || '');
      setFacebook(socialMedia.facebook || '');
      setTiktok(socialMedia.tiktok || '');
    } else {
      // If no profile data yet, default display name to original studio name
      setDisplayName(studioName);
    }
  }, [studioProfileData, studioName]);

  // Helper function to normalize website URL
  const normalizeWebsiteUrl = (url) => {
    if (!url) return '';
    
    let normalized = url.trim();
    
    // Remove protocol if present
    normalized = normalized.replace(/^https?:\/\//, '');
    normalized = normalized.replace(/^www\./, '');
    
    // Add https:// prefix
    return `https://www.${normalized}`;
  };

  // Helper function to normalize Facebook URL
  const normalizeFacebookUrl = (facebookValue) => {
    if (!facebookValue) return '';
    
    let normalized = facebookValue.trim();
    
    // Remove any existing facebook.com or www.facebook.com prefixes
    normalized = normalized
      .replace(/^(https?:\/\/)?(www\.)?facebook\.com\//, '')
      .replace(/^facebook\.com\//, '');
    
    // Return just the clean username/page name (not the full URL for storage)
    return normalized;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setMessage('❌ You must be logged in to manage this profile');
      return;
    }

    // Validate file size
    if (logoFile && logoFile.size > 5 * 1024 * 1024) {
      setMessage('❌ Logo file exceeds maximum size of 5MB');
      return;
    }

    // Validate logo is an image
    if (logoFile && !logoFile.type.startsWith('image/')) {
      setMessage('❌ Logo must be an image file (JPG, PNG)');
      return;
    }

    setSaving(true);
    setMessage('💾 Saving profile changes...');

    try {
      let newLogoURL = currentLogoURL;
      
      // If new logo file is selected, upload it
      if (logoFile) {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const sanitizedStudioName = studioName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
        const logoExtension = logoFile.name.split('.').pop();
        const logoFileName = `${sanitizedStudioName}_studio_logo_${timestamp}.${logoExtension}`;
        const logoRef = ref(storage, `claims/${user.uid}/${logoFileName}`);
        
        const logoUploadTask = uploadBytesResumable(logoRef, logoFile);
        
        await new Promise((resolve, reject) => {
          logoUploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              console.log(`📤 Logo upload progress: ${progress.toFixed(1)}%`);
            },
            (error) => {
              console.error('Logo upload error:', error);
              reject(error);
            },
            async () => {
              try {
                newLogoURL = await getDownloadURL(logoUploadTask.snapshot.ref);
                console.log(`✅ Logo uploaded successfully: ${logoFileName}`);
                resolve();
              } catch (urlError) {
                console.error('Error getting logo download URL:', urlError);
                reject(urlError);
              }
            }
          );
        });
      }
      
      // Normalize website URL if provided
      const normalizedWebsite = website ? normalizeWebsiteUrl(website) : '';
      
      // Process social media handles to remove @ if present
      const processedInstagram = instagram ? instagram.replace('@', '') : '';
      const processedFacebook = facebook ? normalizeFacebookUrl(facebook) : '';
      const processedTiktok = tiktok ? tiktok.replace('@', '') : '';
      
      // Prepare social media object
      const socialMedia = {
        instagram: processedInstagram,
        facebook: processedFacebook,
        tiktok: processedTiktok
      };
      
      // Update studio profile in Firestore
      const studioRef = doc(db, 'studioProfiles', studioName);
      const updateData = {
        displayName: displayName || studioName, // Use display name or fall back to original name
        yearEstablished: yearEstablished || '',
        website: normalizedWebsite,
        description: description || '',
        logoURL: newLogoURL,
        socialMedia,
        lastUpdated: new Date()
      };
      
      await updateDoc(studioRef, updateData);
      
      setMessage('✅ Studio profile updated successfully!');
      setUploadProgress(0);
      setLogoFile(null);
      setCurrentLogoURL(newLogoURL);
      
      // Notify parent component to refresh data
      if (onProfileUpdated) {
        onProfileUpdated();
      }
      
    } catch (error) {
      console.error('Error updating studio profile:', error);
      setMessage(`❌ Error updating profile: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="claim-studio-profile-container">
      <div className="claim-studio-profile-card">
        <h3>🏢 Manage Studio Profile</h3>
        <p>Update your studio's information and logo.</p>
        
        {message && (
          <div className={`message ${message.includes('❌') ? 'error' : message.includes('✅') ? 'success' : 'info'}`}>
            {message}
          </div>
        )}

        {uploadProgress > 0 && uploadProgress < 100 && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span className="progress-text">{uploadProgress.toFixed(1)}% uploaded</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="claim-studio-profile-form">
          {/* Current Logo Display */}
          {currentLogoURL && (
            <div className="form-group">
              <label>Current Studio Logo</label>
              <div style={{ marginTop: '0.5rem' }}>
                <img
                  src={currentLogoURL}
                  alt={`${studioName} current logo`}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    border: '2px solid #ddd',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="logoFile">Update Studio Logo</label>
            <input
              type="file"
              id="logoFile"
              onChange={(e) => setLogoFile(e.target.files[0])}
              accept="image/*"
            />
            <small className="file-help">
              Accepted: Images (JPG, PNG). Max 5MB. Any shape or size.
            </small>
            {logoFile && (
              <div className="file-info">
                Selected: {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Studio Display Name</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How your studio name should appear on the profile"
            />
            <small className="input-help">
              This is how your studio name will be displayed. The original name ({studioName}) will remain in our database for competition data.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="yearEstablished">Year Established</label>
            <input
              type="text"
              id="yearEstablished"
              value={yearEstablished}
              onChange={(e) => setYearEstablished(e.target.value)}
              placeholder="e.g., 2010"
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="e.g., www.example.com"
            />
            <small className="input-help">
              URL will be automatically formatted (https:// added if missing).
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Studio Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us about your studio..."
              rows={4}
            />
          </div>

          <div className="form-section">
            <h4>Social Media (Optional)</h4>
            <div className="social-media-grid">
              <div className="form-group">
                <label htmlFor="instagram">Instagram</label>
                <input
                  type="text"
                  id="instagram"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  placeholder="username (without @)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="facebook">Facebook</label>
                <input
                  type="text"
                  id="facebook"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                  placeholder="Page name or URL"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tiktok">TikTok</label>
                <input
                  type="text"
                  id="tiktok"
                  value={tiktok}
                  onChange={(e) => setTiktok(e.target.value)}
                  placeholder="username (without @)"
                />
              </div>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={saving}
            className="submit-button"
          >
            {saving ? '💾 Saving...' : '💾 Update Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}
